import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiArrowLeft,
  FiFileText,
  FiMapPin,
  FiUser,
  FiBriefcase,
  FiCalendar,
} from "react-icons/fi";
import PropertyComment from "../property/PropertyComment";
import TimeAgo from "../../components/timeAgo/TimeAgo";
import { showToast } from "../../../components/common/notification/Notification";
import styles from "./PostDiscussionPage.module.css";

const PostDiscussionPage = ({ back = "/partner/posts" }) => {
  const { Id } = useParams();
  const navigate = useNavigate();

  const [postData, setPostData] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);

  const loggedInUser = useSelector((state) => state.auth?.user);
  const token =
    loggedInUser?.accessToken ||
    loggedInUser?.token ||
    loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (Id) {
      fetchPostSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Id]);

  const fetchPostSummary = async () => {
    try {
      setLoadingPost(true);
      const response = await axios.get(`${baseUrl}/property/lead/${Id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        const post = response.data.lead || response.data.data || response.data;
        const owner =
          post.user?.id ||
          post.customer?.id ||
          post.partner?.id ||
          post.user_id;

        const rawFirstName =
          post.customer?.first_name ||
          post.partner?.first_name ||
          post.user?.first_name;
        const rawLastName =
          post.customer?.last_name ||
          post.partner?.last_name ||
          post.user?.last_name;
        let cleanName = [rawFirstName, rawLastName].filter(Boolean).join(" ");

        if (!cleanName || cleanName.toLowerCase().includes("null")) {
          cleanName =
            post.customer_name &&
            !post.customer_name.toLowerCase().includes("null")
              ? post.customer_name
              : post.user?.name || "Community Member";
        }

        if (cleanName && typeof cleanName === "string") {
          cleanName = cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
        }

        setPostData({
          postId: post.id || Id,
          ownerId: owner,
          creatorName: cleanName,
          service: post.service,
          city: post.city_name,
          state: post.state_name,
          createdOn: post.created_on || post.created_at,
        });
      }
    } catch (err) {
      console.error("Failed to load post summary for discussion:", err);
      showToast("Error loading post information", { type: "error" });
    } finally {
      setLoadingPost(false);
    }
  };

  const normalizedBack = back.replace(/\/+$/, "");
  const detailsPath = `${normalizedBack}/${Id}`;

  return (
    <div className={styles.wrapper}>
      {/* 1. Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <button
            type="button"
            className={styles.headerBackBtn}
            onClick={() => navigate(detailsPath)}
            aria-label="Back to post details"
            title="Back to post details"
          >
            <FiArrowLeft size={16} />
            <span>Post Details</span>
          </button>
          <div className={styles.headerTitles}>
            <span className={styles.pageCategory}>Community Discussion</span>
            <h1 className={styles.pageTitle}>Post Inquiry & Responses</h1>
          </div>
        </div>
      </div>

      {/* 2. Compact Post Summary Banner */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryLeft}>
          <div className={styles.summaryIconBox}>
            <FiBriefcase />
          </div>
          <div>
            <h3 className={styles.summaryTitle}>
              {postData?.service
                ? `${postData.service} Requirement`
                : `Requirement Post #${Id}`}
            </h3>
            <div className={styles.summaryMeta}>
              {postData?.creatorName && (
                <span className={styles.metaItem}>
                  <FiUser className="text-muted" />
                  <span>Posted by {postData.creatorName}</span>
                </span>
              )}
              {(postData?.city || postData?.state) && (
                <span className={styles.metaItem}>
                  <FiMapPin className="text-danger" />
                  <span>{[postData.city, postData.state].filter(Boolean).join(", ")}</span>
                </span>
              )}
              {postData?.createdOn && (
                <span className={styles.metaItem}>
                  <FiCalendar className="text-muted" />
                  <TimeAgo timestamp={postData.createdOn} />
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.summaryRight}>
          <button
            type="button"
            className={styles.summaryDetailsBtn}
            onClick={() => navigate(detailsPath)}
          >
            <FiFileText size={14} />
            <span>View Full Details</span>
          </button>
        </div>
      </div>

      {/* 3. Discussion & Comment Thread */}
      <div className={styles.discussionMain}>
        <PropertyComment
          Id={Id}
          ownerId={postData?.ownerId}
          commentType="requestComments"
          allComments="/property/requestComments"
          addComment="/property/addRequestComment"
          editComment="/property/editRequestComment"
          deleteComment="/property/deleteRequestComment/"
        />
      </div>
    </div>
  );
};

export default PostDiscussionPage;
